import React, { useState } from 'react';
import jwt_decode from 'jwt-decode';
import { regions, getSelectedRegion } from '../../constants';

function getUserFromToken() {
  try {
    const token = localStorage.getItem('jwtToken');
    if (!token) return null;
    const decoded = jwt_decode(token);
    const email = decoded.email || decoded.id || '';
    const namePart = email.includes('@') ? email.split('@')[0] : email;
    const parts = namePart.replace(/[._-]/g, ' ').split(' ').filter(Boolean);
    const initials = parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : namePart.slice(0, 2).toUpperCase();
    const displayName = parts.map(p => p[0].toUpperCase() + p.slice(1)).join(' ') || email;
    return { name: displayName, email, initials, color: '#e6194b', label: decoded.role || 'User' };
  } catch {
    return null;
  }
}

export const HV_THEME = {
  ink: '#0a0a0a',
  paper: '#ffffff',
  mute: '#6b6b6b',
  line: '#e6e6e6',
  lineSoft: '#f1f1f1',
  bg: '#fafafa',
  chipBg: '#f1f1f1',
  accent: '#e6194b',
  ok: '#16a34a',
  warn: '#d97706',
  danger: '#dc2626',
  info: '#2563eb'
};

export function HVLogo({ size = 28, color = '#fff' }) {
  const w = size, h = size;
  return (
    <svg width={w} height={h} viewBox="0 0 32 32" fill="none" aria-label="HyperVerge">
      <rect x="3" y="2" width="6" height="28" fill={color} />
      <rect x="23" y="2" width="6" height="28" fill={color} />
      <rect x="3" y="14" width="26" height="4" fill={color} />
      <rect x="9" y="2" width="14" height="3" fill={color} />
      <rect x="9" y="27" width="14" height="3" fill={color} />
    </svg>
  );
}

export function HVWordmark({ color = '#fff' }) {
  return (
    <span style={{
      fontFamily: "'Space Grotesk', 'Plus Jakarta Sans', sans-serif",
      fontWeight: 600, fontSize: 18, letterSpacing: '0.18em',
      color, lineHeight: 1
    }}>HYPERVERGE</span>
  );
}

export const Icon = ({ d, size = 16, stroke = 'currentColor', fill = 'none', sw = 1.7, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={stroke}
  strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ flex: '0 0 auto' }}>
    {children || <path d={d} />}
  </svg>
);

export const Icons = {
  search: (p) => <Icon {...p}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></Icon>,
  chevDown: (p) => <Icon {...p}><path d="m6 9 6 6 6-6" /></Icon>,
  chevUp: (p) => <Icon {...p}><path d="m6 15 6-6 6 6" /></Icon>,
  chevL: (p) => <Icon {...p}><path d="m15 6-6 6 6 6" /></Icon>,
  chevR: (p) => <Icon {...p}><path d="m9 6 6 6-6 6" /></Icon>,
  arrowL: (p) => <Icon {...p}><path d="M19 12H5M12 19l-7-7 7-7" /></Icon>,
  refresh: (p) => <Icon {...p}><path d="M3 12a9 9 0 0 1 15.5-6.3L21 8M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15.5 6.3L3 16M3 21v-5h5" /></Icon>,
  lock: (p) => <Icon {...p}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></Icon>,
  unlock: (p) => <Icon {...p}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 7.5-2" /></Icon>,
  plus: (p) => <Icon {...p}><path d="M12 5v14M5 12h14" /></Icon>,
  trash: (p) => <Icon {...p}><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /></Icon>,
  check: (p) => <Icon {...p}><path d="M5 12.5 10 17 19 7" /></Icon>,
  checkCircle: (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="m8 12.5 3 3 5-6" /></Icon>,
  x: (p) => <Icon {...p}><path d="M6 6l12 12M6 18 18 6" /></Icon>,
  xCircle: (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="m9 9 6 6M9 15l6-6" /></Icon>,
  tag: (p) => <Icon {...p}><path d="M20 12 12 20l-9-9V3h8z" /><circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" /></Icon>,
  filter: (p) => <Icon {...p}><path d="M3 5h18M6 12h12M10 19h4" /></Icon>,
  more: (p) => <Icon {...p}><circle cx="5" cy="12" r="1.3" fill="currentColor" /><circle cx="12" cy="12" r="1.3" fill="currentColor" /><circle cx="19" cy="12" r="1.3" fill="currentColor" /></Icon>,
  bell: (p) => <Icon {...p}><path d="M6 8a6 6 0 0 1 12 0c0 7 3 8 3 8H3s3-1 3-8" /><path d="M10 21a2 2 0 0 0 4 0" /></Icon>,
  shield: (p) => <Icon {...p}><path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z" /></Icon>,
  user: (p) => <Icon {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></Icon>,
  logout: (p) => <Icon {...p}><path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l-5-5 5-5M5 12h12" /></Icon>,
  upload: (p) => <Icon {...p}><path d="M12 16V4M7 9l5-5 5 5M4 20h16" /></Icon>,
  folder: (p) => <Icon {...p}><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></Icon>,
  image: (p) => <Icon {...p}><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2" /><path d="m21 16-5-5L5 21" /></Icon>,
  zip: (p) => <Icon {...p}><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M12 7v2M12 11v2M12 15v2" /></Icon>,
  flag: (p) => <Icon {...p}><path d="M5 21V4M5 4h11l-2 4 2 4H5" /></Icon>,
  cog: (p) => <Icon {...p}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></Icon>,
  beaker: (p) => <Icon {...p}><path d="M9 3h6v6l5 11a2 2 0 0 1-1.8 3H5.8A2 2 0 0 1 4 20l5-11z" /><path d="M7 14h10" /></Icon>,
  brain: (p) => <Icon {...p}><path d="M9 4a3 3 0 0 0-3 3v1a3 3 0 0 0-2 2.8c0 1.2.7 2.3 2 2.8V15a3 3 0 0 0 3 3 3 3 0 0 0 3-3V4z" /><path d="M15 4a3 3 0 0 1 3 3v1a3 3 0 0 1 2 2.8 3 3 0 0 1-2 2.8V15a3 3 0 0 1-3 3 3 3 0 0 1-3-3V4z" /></Icon>,
  doc: (p) => <Icon {...p}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></Icon>,
  spark: (p) => <Icon {...p}><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2 2M16 16l2 2M6 18l2-2M16 8l2-2" /></Icon>,
  send: (p) => <Icon {...p}><path d="m22 2-7 20-4-9-9-4z" /><path d="M22 2 11 13" /></Icon>,
  clock: (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>,
  copy: (p) => <Icon {...p}><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></Icon>,
  download: (p) => <Icon {...p}><path d="M12 4v12M7 11l5 5 5-5M4 20h16" /></Icon>,
  external: (p) => <Icon {...p}><path d="M14 4h6v6M20 4 10 14M9 5H4v15h15v-5" /></Icon>,
  history: (p) => <Icon {...p}><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5M12 7v5l3 2" /></Icon>,
  alert: (p) => <Icon {...p}><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></Icon>,
  home: (p) => <Icon {...p}><path d="M3 12L12 3l9 9M5 10v9a1 1 0 0 0 1 1h4v-5h4v5h4a1 1 0 0 0 1-1v-9" /></Icon>
};

export function HVHeader({ tab = 'projects', env = 'STAGING', role = {}, onTab, showTabs = true, onLogout }) {
  const tabs = [{ id: 'projects', label: 'PROJECTS' }, { id: 'groups', label: 'GROUPS' }, { id: 'models', label: 'MODELS' }];
  const tokenUser = getUserFromToken();
  const profile = tokenUser || (role && role.name ? role : { name: 'User', initials: 'U', color: '#e6194b', label: 'User' });
  const [selectedRegion, setSelectedRegion] = useState(getSelectedRegion());
  const isProd = !['stg', 'dev'].includes(selectedRegion);

  const handleRegionChange = (e) => {
    const r = e.target.value;
    localStorage.setItem('REGION', r);
    setSelectedRegion(r);
    const url = new URL(window.location.href);
    url.searchParams.set('region', r);
    window.location.href = url.toString();
  };

  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 20 }}>
      <div style={{
        background: '#000', color: '#fff', height: 64,
        display: 'flex', alignItems: 'center', padding: '0 28px', gap: 16
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => { window.location.href = '/'; }}>
          <HVLogo size={28} />
          <HVWordmark />
        </div>
        <div style={{ flex: 1 }} />

        {/* Region selector */}
        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          <span style={{ position: 'absolute', left: 10, width: 6, height: 6, borderRadius: 99, background: isProd ? HV_THEME.accent : '#facc15', pointerEvents: 'none', zIndex: 1 }} />
          <select
            value={selectedRegion}
            onChange={handleRegionChange}
            style={{
              appearance: 'none', background: '#1a1a1a', color: '#fff',
              border: '1px solid #2a2a2a', borderRadius: 4,
              padding: '8px 30px 8px 24px', fontSize: 11,
              letterSpacing: '0.14em', fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit', minWidth: 130
            }}
          >
            {Object.entries(regions).map(([key, label]) => (
              <option key={key} value={key} style={{ background: '#1a1a1a' }}>{label}</option>
            ))}
          </select>
          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><path d="M6 9l6 6 6-6" /></svg>
        </div>

        <HVTopBtn icon={<Icons.home size={12}/>} onClick={() => { window.location.href = '/'; }}>HOME</HVTopBtn>
        <HVTopBtn onClick={() => { window.location.href = '/management'; }}>MANAGEMENT</HVTopBtn>
<HVTopBtn onClick={() => { window.location.href = '/tasks'; }}>TASKS</HVTopBtn>
        <div style={{ width: 1, height: 28, background: '#2a2a2a' }} />
        <ProfileChip role={profile} />
        <button onClick={onLogout} title="Logout" style={{
          width: 36, height: 36, borderRadius: 4, background: 'transparent', color: '#fff',
          border: '1px solid #2a2a2a', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
        }}><Icons.logout size={15} /></button>
      </div>
      {showTabs &&
      <div style={{ background: '#000', borderTop: '1px solid #1c1c1c', display: 'flex', padding: '0 24px', height: 44 }}>
          {tabs.map((t) => {
          const active = t.id === tab;
          return (
            <button key={t.id} onClick={() => onTab && onTab(t.id)} style={{
              background: 'transparent', border: 0, color: active ? '#fff' : '#9a9a9a',
              fontSize: 11, letterSpacing: '0.14em', fontWeight: 600, padding: '0 20px',
              position: 'relative', cursor: 'pointer', fontFamily: 'inherit'
            }}>
                {t.label}
                {active && <div style={{ position: 'absolute', left: 14, right: 14, bottom: 0, height: 3, background: HV_THEME.accent }} />}
              </button>);
        })}
        </div>
      }
    </div>
  );
}

export function HVTopBtn({ children, onClick, icon }) {
  return (
    <button onClick={onClick} style={{
      background: 'transparent', color: '#fff', border: '1px solid #2a2a2a',
      borderRadius: 4, padding: '9px 16px', fontSize: 11, letterSpacing: '0.14em',
      fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
      display: 'inline-flex', alignItems: 'center', gap: 7
    }}>{icon}{children}</button>
  );
}

export function ProfileChip({ role }) {
  if (!role || !role.name) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ textAlign: 'right', lineHeight: 1.35 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>{role.name}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
          {role.label && role.label !== 'User' && (
            <span style={{ fontSize: 10, fontWeight: 700, color: role.color || HV_THEME.accent, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{role.label}</span>
          )}
          {role.email && (
            <span style={{ fontSize: 10, color: '#9a9a9a', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {role.email.split('@')[0]}</span>
          )}
        </div>
      </div>
      <div style={{
        width: 36, height: 36, borderRadius: 99, background: role.color || '#2563eb',
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 600, fontSize: 12, letterSpacing: '0.04em', flexShrink: 0
      }}>{role.initials || role.name[0]}</div>
    </div>
  );
}

export function HVButton({ variant = 'primary', size = 'md', children, icon, onClick, disabled, full, style = {} }) {
  const sizes = {
    sm: { padding: '6px 12px', fontSize: 11, height: 30, gap: 6 },
    md: { padding: '10px 16px', fontSize: 12, height: 38, gap: 8 },
    lg: { padding: '12px 20px', fontSize: 13, height: 44, gap: 10 }
  };
  const variants = {
    primary: { background: '#000', color: '#fff', border: '1px solid #000' },
    secondary: { background: '#fff', color: '#000', border: '1px solid #000' },
    ghost: { background: 'transparent', color: '#000', border: '1px solid #d4d4d4' },
    danger: { background: '#fff', color: HV_THEME.danger, border: `1px solid ${HV_THEME.danger}` },
    success: { background: HV_THEME.ok, color: '#fff', border: `1px solid ${HV_THEME.ok}` },
    accent: { background: HV_THEME.accent, color: '#fff', border: `1px solid ${HV_THEME.accent}` }
  };
  const s = sizes[size];
  const v = variants[variant];
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...s, ...v,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: s.gap,
      letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600, fontFamily: 'inherit',
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1,
      width: full ? '100%' : 'auto', whiteSpace: 'nowrap', borderRadius: 2,
      ...style
    }}>
      {icon}{children}
    </button>
  );
}

export function StatusPill({ tone = 'neutral', children, dot = true, size = 'md' }) {
  const tones = {
    neutral: { bg: '#f1f1f1', fg: '#3a3a3a', dot: '#9a9a9a' },
    ok: { bg: '#e7f5ed', fg: '#0f5d2a', dot: HV_THEME.ok },
    warn: { bg: '#fdf1dc', fg: '#7a4a07', dot: HV_THEME.warn },
    danger: { bg: '#fce8e9', fg: '#7a1418', dot: HV_THEME.danger },
    info: { bg: '#e6efff', fg: '#1a3a8a', dot: HV_THEME.info },
    accent: { bg: '#fde6ed', fg: '#8a0f30', dot: HV_THEME.accent },
    purple: { bg: '#efe6ff', fg: '#4a1a8a', dot: '#7c3aed' }
  }[tone];
  const ss = size === 'sm' ? { p: '2px 7px', fs: 10 } : { p: '3px 9px', fs: 11 };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, background: tones.bg,
      color: tones.fg, padding: ss.p, borderRadius: 99, fontSize: ss.fs,
      fontWeight: 600, letterSpacing: '0.04em', whiteSpace: 'nowrap'
    }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: 99, background: tones.dot }} />}
      {children}
    </span>
  );
}

export function HVInput({ icon, value, onChange, placeholder, suffix, style = {}, disabled, ...rest }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, background: disabled ? '#f4f4f4' : '#fff',
      border: '1px solid #d4d4d4', borderRadius: 2, padding: '0 12px',
      height: 38, opacity: disabled ? 0.7 : 1, ...style
    }}>
      {icon}
      <input value={value} onChange={onChange} placeholder={placeholder} disabled={disabled} {...rest} style={{
        flex: 1, border: 0, outline: 0, background: 'transparent', fontSize: 13,
        fontFamily: 'inherit', color: HV_THEME.ink
      }} />
      {suffix}
    </div>
  );
}

export function Card({ children, style = {}, pad = 20 }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #ececec', borderRadius: 4,
      padding: pad, ...style
    }}>{children}</div>
  );
}

export function SectionTitle({ eyebrow, title, subtitle, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 14 }}>
      <div>
        {eyebrow && <div style={{ fontSize: 10, letterSpacing: '0.16em', fontWeight: 700, color: HV_THEME.mute, textTransform: 'uppercase', marginBottom: 6 }}>{eyebrow}</div>}
        <div style={{ fontSize: 22, fontWeight: 700, color: HV_THEME.ink, letterSpacing: '-0.01em' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: HV_THEME.mute, marginTop: 4 }}>{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}

export function Spinner({ size = 20, color = HV_THEME.ink }) {
  return (
    <>
      <style>{`@keyframes hv-spin{to{transform:rotate(360deg)}}`}</style>
      <svg width={size} height={size} viewBox="0 0 50 50" style={{ animation: 'hv-spin 0.9s linear infinite', flex: '0 0 auto' }}>
        <circle cx="25" cy="25" r="20" fill="none" stroke={color} strokeOpacity="0.15" strokeWidth="5" />
        <circle cx="25" cy="25" r="20" fill="none" stroke={color} strokeWidth="5"
          strokeLinecap="round" strokeDasharray="60 200" />
      </svg>
    </>
  );
}

export function ContentLoader({ title = 'Loading', subtitle, size = 'md', inline = false, tone = 'default' }) {
  const sizes = {
    sm: { spinner: 16, pad: 18, gap: 10, ts: 12, ss: 10 },
    md: { spinner: 28, pad: 56, gap: 14, ts: 14, ss: 12 },
    lg: { spinner: 40, pad: 96, gap: 18, ts: 16, ss: 13 },
  }[size];
  return (
    <div style={{
      display: 'flex', flexDirection: inline ? 'row' : 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: sizes.gap, padding: inline ? 0 : `${sizes.pad}px 20px`,
      textAlign: 'center', color: HV_THEME.mute,
      minHeight: inline ? 'auto' : 240,
    }}>
      <Spinner size={sizes.spinner} color={tone === 'brand' ? HV_THEME.accent : HV_THEME.ink} />
      <div style={{ textAlign: inline ? 'left' : 'center' }}>
        <div style={{ fontSize: sizes.ts, fontWeight: 600, color: HV_THEME.ink }}>{title}</div>
        {subtitle && <div style={{ fontSize: sizes.ss, color: HV_THEME.mute, marginTop: 3 }}>{subtitle}</div>}
      </div>
    </div>
  );
}

export function ScreenEmpty({ role = {}, kind = 'projects', onAction }) {
  const KINDS = {
    projects: {
      label: 'Projects',
      title: 'No projects yet',
      body: 'Create your first AI extraction project to start training and managing LLM models.',
      icon: <Icons.folder size={28} />,
      illoIcon: <Icons.folder size={28} stroke="#fff" sw={1.5} />,
      primary: 'New project',
      primaryIcon: <Icons.plus size={14} />,
    },
    groups: {
      label: 'Groups',
      title: 'No groups yet',
      body: 'Groups bundle multiple projects together so you can deploy them as a single endpoint.',
      icon: <Icons.folder size={28} />,
      illoIcon: <Icons.folder size={28} stroke="#fff" sw={1.5} />,
      primary: 'New group',
      primaryIcon: <Icons.plus size={14} />,
    },
    search: {
      label: 'Search results',
      title: 'No results found',
      body: 'We couldn’t find anything matching your search. Try adjusting your filters or check the spelling of your query.',
      icon: <Icons.search size={28} />,
      illoIcon: <Icons.search size={28} stroke="#fff" sw={1.5} />,
      primary: 'Clear filters',
      primaryIcon: <Icons.x size={14} />,
    }
  };
  const k = KINDS[kind];

  return (
    <Card pad={0} style={{ overflow: 'hidden', margin: '20px 0', position: 'relative' }}>
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'radial-gradient(#e0e0e0 1px, transparent 1px)',
        backgroundSize: '18px 18px', opacity: 0.5, pointerEvents: 'none'
      }} />
      <div style={{ position: 'relative', padding: '64px 48px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ position: 'relative', marginBottom: 28 }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <SlotFrame faded />
            <SlotFrame primary icon={k.illoIcon} tone='ink' />
            <SlotFrame faded />
          </div>
        </div>

        <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 8 }}>{k.title}</div>
        <div style={{ fontSize: 14, color: HV_THEME.mute, maxWidth: 520, lineHeight: 1.55, marginBottom: 24, textWrap: 'pretty' }}>{k.body}</div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
          {onAction && (
            <HVButton onClick={onAction} variant="primary" icon={k.primaryIcon} size="lg">{k.primary}</HVButton>
          )}
        </div>
      </div>
    </Card>
  );
}

export function HVSelect({ value, onChange, children, style = {}, placeholder }) {
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <select
        value={value}
        onChange={onChange}
        style={{
          width: '100%', height: 38, border: '1px solid #d4d4d4', borderRadius: 2,
          padding: '0 32px 0 12px', fontFamily: 'inherit', fontSize: 13,
          color: HV_THEME.ink, outline: 0, appearance: 'none', WebkitAppearance: 'none',
          background: '#fff', cursor: 'pointer', boxSizing: 'border-box', ...style,
        }}
      >
        {placeholder && <option value="" disabled>{placeholder}</option>}
        {children}
      </select>
      <svg
        width={14} height={14} viewBox="0 0 24 24" fill="none"
        stroke={HV_THEME.mute} strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"
        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
  );
}

function SlotFrame({ faded, primary, icon, tone = 'ink' }) {
  const size = primary ? 96 : 76;
  const bg = primary ? '#000' : 'transparent';
  return (
    <div style={{
      width: size, height: size, borderRadius: 8,
      background: bg, color: '#fff',
      border: primary ? 0 : '2px dashed #d4d4d4',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      opacity: faded ? 0.7 : 1, position: 'relative',
      boxShadow: primary ? '0 8px 24px rgba(0,0,0,0.12)' : 'none'
    }}>
      {primary && icon}
      {!primary && <div style={{ width: 24, height: 24, borderRadius: 4, background: '#ececec' }} />}
      {primary &&
        <span style={{
          position: 'absolute', top: -8, right: -8, background: HV_THEME.accent,
          color: '#fff', width: 22, height: 22, borderRadius: 99,
          fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>+</span>
      }
    </div>
  );
}
